import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import {
  AppLink,
  Button,
  Callout,
  DataTable,
  ReadOnlyNote,
  ReasonDialog,
  SectionHeader,
  SegmentedFilter,
  Toolbar,
  ToolbarSpacer,
  dataColumns,
  errorText,
  formatDateTime,
  type DataColumn,
} from "@amber/ui";
import type { DeletedResponse } from "@amber/application/contracts";
import type { DeletedRow } from "@amber/collection/contracts";
import {
  accessPreviewQuery,
  collectionCommands,
  AFTER_COLLECTION_CHANGE,
  ON_COLLECTION_CONFLICT,
} from "@amber/collection/ui";
import { profileCommands } from "@amber/profiles/ui";
import { deletedQuery } from "./queries";

function RestoreProfileDialog({ row, onClose }: { row: DeletedRow; onClose: () => void }) {
  return (
    <ReasonDialog
      title={`Restore ${row.name}?`}
      confirm="Restore"
      success={`Restored: ${row.name}`}
      onClose={onClose}
      run={(reason, csrf) => profileCommands.restore(row.id, { reason }, csrf)}
      invalidate={AFTER_COLLECTION_CHANGE}
      invalidateOnConflict={ON_COLLECTION_CONFLICT}
    >
      The draft profile becomes visible again in the roster, searches and exports, with the status it had. Suggested
      sources discarded when it was deleted stay discarded; source discovery can run again from the profile.
    </ReasonDialog>
  );
}

function RestoreDialog({ row, tab, onClose }: { row: DeletedRow; tab: "deleted" | "restricted"; onClose: () => void }) {
  const preview = useQuery(accessPreviewQuery(row.id, "normal"));
  const verb = tab === "deleted" ? "Restore" : "Lift restriction";
  return (
    <ReasonDialog
      title={`${verb}?`}
      confirm={verb}
      success={tab === "deleted" ? `Restored: ${row.name}` : `Restriction lifted: ${row.name}`}
      onClose={onClose}
      run={(reason, csrf) => collectionCommands.changeAccess(row.id, "restore", { reason }, csrf)}
      invalidate={AFTER_COLLECTION_CHANGE}
      invalidateOnConflict={ON_COLLECTION_CONFLICT}
      extra={
        <ul className="m-0 mb-1 list-disc pl-5 text-muted">
          {(preview.data?.effects ?? ["Preparing the preview…"]).map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      }
    >
      <b>{row.name}</b> · {row.scope}
    </ReasonDialog>
  );
}

const col = dataColumns<DeletedRow>();

function deletedColumns(
  tab: "deleted" | "restricted",
  disabledReason: string | null,
  onRestore: (row: DeletedRow) => void,
): DataColumn<DeletedRow>[] {
  return [
    col.display({
      id: "name",
      header: "Name",
      meta: { width: 300, cellClassName: "h-12" },
      cell: ({ row: { original: row } }) => (
        <>
          <span className="block truncate font-strong text-fg" title={row.name}>
            {row.name}
          </span>
          <span className="block truncate text-md text-muted">{row.scope}</span>
        </>
      ),
    }),
    col.display({
      id: "type",
      header: "Type",
      meta: { width: 80, cellClassName: "capitalize" },
      cell: ({ row }) => row.original.type,
    }),
    col.display({
      id: "by",
      header: tab === "deleted" ? "Deleted by" : "Restricted by",
      meta: { width: 130 },
      cell: ({ row }) => row.original.by,
    }),
    col.display({
      id: "when",
      header: "When",
      meta: { width: 120 },
      cell: ({ row }) => formatDateTime(row.original.at),
    }),
    col.display({ id: "reason", header: "Reason", cell: ({ row }) => row.original.reason }),
    col.display({
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { label: "Actions", width: 110, cellClassName: "overflow-visible text-right" },
      cell: ({ row: { original: row } }) => (
        <Button
          size="xs"
          disabledReason={disabledReason}
          onClick={() => {
            onRestore(row);
          }}
        >
          {tab === "deleted" ? (
            <>
              <RotateCcw aria-hidden="true" />
              Restore…
            </>
          ) : (
            "Lift…"
          )}
        </Button>
      ),
    }),
  ];
}

type Tab = "deleted" | "restricted";

const EMPTY: Record<Tab, string> = { deleted: "No deleted items", restricted: "Nothing is restricted" };
const TABLE_LABEL: Record<Tab, string> = { deleted: "Deleted items", restricted: "Restricted items" };
const TAB_NOTE: Record<Tab, string> = {
  deleted: "Hidden from all views, reads and exports; kept in the archive.",
  restricted: "Hidden from the named uses; visible to Admins with a banner.",
};

function DeletedMeta({ data }: { data: DeletedResponse }) {
  return (
    <>
      <span>
        Archive size <b>{data.archive.label}</b>
      </span>
      <AppLink href="/settings/audit?action=collection" className="font-strong text-link">
        Audit log
      </AppLink>
      {data.rows === null ? <ReadOnlyNote>Only Admins can see what was deleted or restricted</ReadOnlyNote> : null}
    </>
  );
}

function CountsOnly({ counts }: { counts: DeletedResponse["counts"] }) {
  return (
    <div className="mx-4 flex max-w-[720px] flex-col gap-3 max-compact:mx-2">
      <Callout tone="neutral" title="Only Admins can see what was deleted or restricted.">
        Everyone can read the rest of Settings. Deleted and restricted content is hidden from every view, read and
        export for other roles, so only the counts are shown here. Nothing here is erased; an Admin can restore it.
      </Callout>
      <dl className="m-0 grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 rounded-xl border border-divider bg-bg p-3 text-ui [&_dd]:m-0 [&_dt]:text-muted">
        <dt>Deleted</dt>
        <dd>
          {counts.deleted} {counts.deleted === 1 ? "item" : "items"}
        </dd>
        <dt>Restricted</dt>
        <dd>
          {counts.restricted} {counts.restricted === 1 ? "item" : "items"}
        </dd>
        <dt>Who to ask</dt>
        <dd>An Admin (see Users and roles)</dd>
      </dl>
    </div>
  );
}

export function DeletedItems() {
  const [tab, setTab] = useState<"deleted" | "restricted">("deleted");
  const query = useQuery(deletedQuery(tab));
  const [restoring, setRestoring] = useState<DeletedRow | null>(null);
  const data = query.data;
  const disabledReason = data && !data.restore.enabled ? data.restore.reason : null;
  const columns = useMemo(() => deletedColumns(tab, disabledReason, setRestoring), [tab, disabledReason]);
  return (
    <section aria-labelledby="section-title" className="flex min-h-0 flex-1 flex-col">
      <SectionHeader
        titleId="section-title"
        title="Deleted items"
        lead="Deleted things are hidden from all views, reads and exports, but kept, and an Admin can restore them here. They aren’t counted in content or delivery figures, but still count in archive size and spending (“incl. N deleted”). Permanent erasure is only done for legal reasons, outside this app, and appears in the audit log."
        meta={data ? <DeletedMeta data={data} /> : null}
      />
      {query.isError ? (
        <Callout tone="attention" role="alert" className="mx-4 mb-2">
          Couldn&apos;t load deleted items. {errorText(query.error)}
        </Callout>
      ) : null}
      {data && data.rows === null ? (
        <CountsOnly counts={data.counts} />
      ) : (
        <>
          <Toolbar>
            <SegmentedFilter
              label="Show"
              value={tab}
              onChange={setTab}
              options={[
                { value: "deleted", label: "Deleted", count: data?.counts.deleted ?? 0 },
                { value: "restricted", label: "Restricted", count: data?.counts.restricted ?? 0 },
              ]}
            />
            <span className="text-ui text-muted">{TAB_NOTE[tab]}</span>
            <ToolbarSpacer />
          </Toolbar>
          <DataTable
            label={TABLE_LABEL[tab]}
            columns={columns}
            rows={data?.rows ?? []}
            rowId={(row) => row.id}
            minWidth={960}
            pinned={["name"]}
            rowOptions={(row) => ({ titles: { reason: row.reason } })}
            isPending={query.isPending}
            skeletonRows={3}
            empty={data?.rows ? EMPTY[tab] : null}
          />
        </>
      )}
      {restoring?.type === "profile" ? (
        <RestoreProfileDialog
          row={restoring}
          onClose={() => {
            setRestoring(null);
          }}
        />
      ) : null}
      {restoring && restoring.type !== "profile" ? (
        <RestoreDialog
          row={restoring}
          tab={tab}
          onClose={() => {
            setRestoring(null);
          }}
        />
      ) : null}
    </section>
  );
}
